import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { useTheme } from '../../../contexts/ThemeContext'
import useDataStaleness from '../hooks/useDataStaleness'
import AccountSwitcher from '../../../shared/components/AccountSwitcher'

const navigation = [
  {
    name: 'Dashboard',
    href: '/global',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="9" rx="1.5" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        <rect x="3" y="16" width="7" height="5" rx="1.5" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        <rect x="14" y="12" width="7" height="9" rx="1.5" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    name: 'Organizations',
    href: '/global/organizations',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
      </svg>
    )
  },
  {
    name: 'Policy Library',
    href: '/global/policy-templates',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    )
  },
  {
    name: 'Activity & Security',
    href: '/global/activity',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  {
    name: 'Insurance Review Audit',
    href: '/global/insurance-review-audit',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 3v16.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V7.5L15 3H5.25a1.5 1.5 0 00-1.5 1.5z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h8M8 16h5" />
      </svg>
    )
  },
  {
    name: 'Platform Metrics',
    href: '/global/status',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12h4l3-9 4 18 3-9h4" />
      </svg>
    )
  },
  {
    name: 'Notifications',
    href: '/global/notifications',
    hasBadge: true,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    )
  },
  {
    name: 'Platform Settings',
    href: '/global/settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )
  }
]

// Removed adminDashboardItem — replaced by org switcher at bottom of sidebar

function GlobalAdminLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, profile, signOut } = useAuth()
  const { darkMode, toggleDarkMode } = useTheme()
  const { count: notificationCount } = useDataStaleness()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarPinned, setSidebarPinned] = useState(() => localStorage.getItem('global-sidebar-pinned') === 'true')
  const [sidebarHovered, setSidebarHovered] = useState(false)
  const sidebarExpanded = sidebarPinned || sidebarHovered
  const sidebarCollapsed = !sidebarExpanded
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const handleSignOut = async () => {
    setShowDropdown(false)
    await signOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
          transform transition-all duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ backgroundColor: '#012438', borderRight: '1px solid #3F5A6A' }}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center h-16 ${sidebarCollapsed ? 'px-3 justify-center' : 'px-6'}`}>
            <Link to="/global" className={`flex items-center ${sidebarCollapsed ? '' : 'space-x-3'}`}>
              <img src="/logo.png" alt="SSA Logo" className="w-9 h-9 rounded-xl object-contain flex-shrink-0" />
              {!sidebarCollapsed && (
                <div className="flex flex-col">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-bold text-white tracking-wide">Vantage</span>
                    <span className="text-[9px] font-normal" style={{ color: '#6B7F8E' }}>by Sidewalk Security</span>
                  </div>
                  <span className="text-[8px] font-medium uppercase tracking-wider" style={{ color: '#8A9BAA' }}>Executive Advisory Platform</span>
                </div>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 ${sidebarCollapsed ? 'px-2' : 'px-4'} py-4 space-y-1 overflow-y-auto scrollbar-hide`}>
            {navigation.map((item) => {
              const isActive = item.href === '/global'
                ? location.pathname === '/global'
                : location.pathname.startsWith(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  title={sidebarCollapsed ? item.name : undefined}
                  className={`
                    flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'} rounded-lg text-sm font-medium
                    transition-all duration-200 group relative
                  `}
                  style={isActive
                    ? { backgroundColor: '#213C4E', color: '#ffffff' }
                    : { color: '#BCC5CD' }
                  }
                  onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' } }}
                  onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BCC5CD' } }}
                >
                  <span className={`${sidebarCollapsed ? '' : 'mr-3'} transition-colors flex-shrink-0 relative`}
                    style={{ color: isActive ? '#8AD0D6' : '#7E8F9C' }}>
                    {item.icon}
                    {item.hasBadge && notificationCount > 0 && sidebarCollapsed && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {notificationCount}
                      </span>
                    )}
                  </span>
                  {!sidebarCollapsed && (
                    <>
                      {item.name}
                      {item.hasBadge && notificationCount > 0 ? (
                        <span className="ml-auto bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                          {notificationCount}
                        </span>
                      ) : isActive ? (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8AD0D6' }} />
                      ) : null}
                    </>
                  )}
                </Link>
              )
            })}
          </nav>

          {/* Account Switcher */}
          <AccountSwitcher collapsed={sidebarCollapsed} currentView="global" />

          {/* Collapse toggle */}
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} py-3`}>
            <div className="mx-2 mb-2" style={{ borderTop: '1px solid rgba(63, 90, 106, 0.4)' }} />
            <button
              onClick={() => {
                const next = !sidebarPinned
                setSidebarPinned(next)
                localStorage.setItem('global-sidebar-pinned', next ? 'true' : 'false')
                if (!next) setSidebarHovered(false)
              }}
              className="w-full flex items-center justify-center px-3 py-2 rounded-lg text-sm transition-colors"
              style={{ color: '#7E8F9C' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#BCC5CD' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#7E8F9C' }}
              title={sidebarPinned ? 'Collapse sidebar' : 'Sidebar control'}
            >
              {sidebarCollapsed ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
              ) : (
                <>
                  <svg className={`w-5 h-5 transition-transform duration-300 ${sidebarPinned ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                  <span className="ml-3">{sidebarPinned ? 'Collapse' : 'Expand'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className={`${sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'} flex flex-col min-h-screen transition-all duration-200`}>
        {/* Top bar */}
        <header data-main-header className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900 backdrop-blur-md dark:backdrop-blur-none border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Left side */}
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="hidden lg:flex flex-col">
                <span className="text-[11px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  Sidewalk Security Advisors
                </span>
                <span className="text-[9px] font-medium uppercase tracking-wider text-gray-300 dark:text-gray-600">
                  Global Admin Portal
                </span>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-2">
              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* User dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {profile?.full_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <svg className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 min-w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{profile?.full_name || 'User'}</p>
                      {profile?.role && (
                        <p className="text-[11px] font-medium text-primary-600 dark:text-primary-400 mt-0.5 whitespace-nowrap">
                          {profile.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white/50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
              Sidewalk Security Advisors • Global Administration
            </p>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default GlobalAdminLayout
